import React, { FC, useState } from 'react';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import Star from '@material-ui/icons/Star';
import StarBorderOutlined from '@material-ui/icons/StarBorderOutlined';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import type {
  UnitTypeRatingCount,
  UnitTypeReview
} from '../../../../../shared/models/unit-types.model';

interface ReviewDraft {
  name: string;
  stayedNights: string;
  rating: number;
  date: string;
  description: string;
}

export interface RatingsReviewsProps {
  data: {
    rating: number;
    reviewsCount: number;
    ratings: UnitTypeRatingCount[];
    reviews: UnitTypeReview[];
  };
  eventHandlers: {
    updateRatings: (ratings: UnitTypeRatingCount[]) => void;
    updateReviews: (reviews: UnitTypeReview[]) => void;
  };
}

const createEmptyReview = (): ReviewDraft => ({
  name: '',
  stayedNights: '1',
  rating: 5,
  date: new Date().toISOString().slice(0, 10),
  description: ''
});

const formatReviewDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
};

const dateInputValue = (timestamp: number) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const StarRating: FC<{
  value: number;
  onChange?: (value: number) => void;
  label: string;
}> = ({ value, onChange, label }) => (
  <div
    className="flex items-center gap-1"
    role={onChange ? 'radiogroup' : 'img'}
    aria-label={label}
  >
    {[1, 2, 3, 4, 5].map((starValue) => {
      const isFilled = value >= starValue;
      const icon = isFilled ? (
        <Star className="text-amber-400" style={{ fontSize: 24 }} />
      ) : (
        <StarBorderOutlined className="text-gray-300" style={{ fontSize: 24 }} />
      );

      return onChange ? (
        <button
          key={starValue}
          type="button"
          role="radio"
          aria-checked={value === starValue}
          aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
          className="flex rounded focus:outline-none focus:ring-2 focus:ring-primary-300"
          onClick={() => onChange(starValue)}
        >
          {icon}
        </button>
      ) : (
        <span key={starValue} className="flex">
          {icon}
        </span>
      );
    })}
  </div>
);

export const RatingsReviews: FC<RatingsReviewsProps> = ({ data, eventHandlers }) => {
  const [ratings, setRatings] = useState<UnitTypeRatingCount[]>(data.ratings);
  const [ratingDraft, setRatingDraft] = useState({ rating: '', count: '' });
  const [reviews, setReviews] = useState<UnitTypeReview[]>(data.reviews);
  const [reviewDraft, setReviewDraft] = useState<ReviewDraft>(createEmptyReview());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [hasEditedRatings, setHasEditedRatings] = useState(false);
  const [hasPendingRatingUpdates, setHasPendingRatingUpdates] = useState(false);
  const [hasPendingReviewUpdates, setHasPendingReviewUpdates] = useState(false);

  const calculatedReviewsCount = ratings.reduce((total, item) => total + item.count, 0);
  const calculatedRating = calculatedReviewsCount
    ? ratings.reduce((total, item) => total + item.rating * item.count, 0) / calculatedReviewsCount
    : 0;

  const rating = ratings.length
    ? Number(calculatedRating.toFixed(1))
    : hasEditedRatings
    ? 0
    : data.rating;

  const reviewsCount = ratings.length
    ? calculatedReviewsCount
    : hasEditedRatings
    ? 0
    : data.reviewsCount;

  const addRating = () => {
    const ratingValue = Number(ratingDraft.rating);
    const countValue = Number(ratingDraft.count);

    if (!Number.isFinite(ratingValue) || ratingValue < 1 || ratingValue > 5) {
      birdToast.error('Rating must be a number between 1 and 5.');
      return;
    }

    if (!Number.isInteger(countValue) || countValue < 1) {
      birdToast.error('Review count must be a whole number greater than zero.');
      return;
    }

    if (ratings.some((item) => item.rating === ratingValue)) {
      birdToast.error('This rating already exists. Delete it first or use another rating.');
      return;
    }

    setRatings((currentRatings) =>
      [...currentRatings, { rating: ratingValue, count: countValue }].sort(
        (first, second) => second.rating - first.rating
      )
    );
    setRatingDraft({ rating: '', count: '' });
    setHasEditedRatings(true);
    setHasPendingRatingUpdates(true);
  };

  const deleteRating = (index: number) => {
    setRatings((currentRatings) =>
      currentRatings.filter((_, ratingIndex) => ratingIndex !== index)
    );
    setHasEditedRatings(true);
    setHasPendingRatingUpdates(true);
  };

  const updateDraft = (changes: Partial<ReviewDraft>) => {
    setReviewDraft((currentDraft) => ({ ...currentDraft, ...changes }));
  };

  const resetReviewForm = () => {
    setReviewDraft(createEmptyReview());
    setEditingIndex(null);
  };

  const submitReview = () => {
    const name = reviewDraft.name.trim();
    const description = reviewDraft.description.trim();
    const stayedNights = Number(reviewDraft.stayedNights);
    const date = new Date(`${reviewDraft.date}T00:00:00`).getTime();

    if (!name || !description || !reviewDraft.date) {
      birdToast.error('Reviewer name, review date, and review text are required.');
      return;
    }

    if (!Number.isInteger(stayedNights) || stayedNights < 1) {
      birdToast.error('Stayed nights must be a whole number greater than zero.');
      return;
    }

    const review: UnitTypeReview = {
      ...(editingIndex !== null && reviews[editingIndex].id
        ? { id: reviews[editingIndex].id }
        : {}),
      name,
      stayedNights,
      rating: reviewDraft.rating,
      date,
      description
    };

    setReviews((currentReviews) => {
      if (editingIndex === null) return [...currentReviews, review];
      return currentReviews.map((currentReview, index) =>
        index === editingIndex ? review : currentReview
      );
    });
    setHasPendingReviewUpdates(true);
    resetReviewForm();
  };

  const editReview = (review: UnitTypeReview, index: number) => {
    setReviewDraft({
      name: review.name,
      stayedNights: String(review.stayedNights),
      rating: review.rating,
      date: dateInputValue(review.date),
      description: review.description
    });
    setEditingIndex(index);
  };

  const deleteReview = (index: number) => {
    setReviews((currentReviews) =>
      currentReviews.filter((_, reviewIndex) => reviewIndex !== index)
    );
    setHasPendingReviewUpdates(true);
    if (editingIndex === index) resetReviewForm();
  };

  const saveRatings = () => {
    eventHandlers.updateRatings(ratings);
    setHasPendingRatingUpdates(false);
  };

  const saveReviews = () => {
    eventHandlers.updateReviews(reviews);
    setHasPendingReviewUpdates(false);
  };

  const displayedReview =
    editingIndex === null
      ? {
          name: reviewDraft.name || 'Guest name',
          stayedNights: Number(reviewDraft.stayedNights) || 1,
          rating: reviewDraft.rating,
          date: reviewDraft.date ? new Date(`${reviewDraft.date}T00:00:00`).getTime() : Date.now(),
          description:
            reviewDraft.description || 'The guest review will appear here as your team enters it.'
        }
      : reviews[editingIndex];

  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:p-8">
      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="m-0 font-gilroy-medium text-2xl font-semibold text-gray-800">
                Rating summary
              </h3>
              <p className="mt-1 text-lg leading-7 text-gray-600">
                These values are shown in the summary card on the website.
              </p>
            </div>
            <Button
              onClick={saveRatings}
              size={ButtonSize.small}
              disabled={!hasPendingRatingUpdates}
              tracking={{ action: 'save ratings', category: 'unit type', label: '' }}
            >
              Save ratings
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <label className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Rating
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={ratingDraft.rating}
                placeholder="e.g. 4.5"
                onChange={(event) => {
                  setRatingDraft((current) => ({ ...current, rating: event.target.value }));
                }}
                className="h-12 rounded-lg border border-gray-300 bg-white px-4 text-lg text-gray-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <label className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Review count
              <input
                type="number"
                min="1"
                step="1"
                value={ratingDraft.count}
                placeholder="e.g. 12"
                onChange={(event) => {
                  setRatingDraft((current) => ({ ...current, count: event.target.value }));
                }}
                className="h-12 rounded-lg border border-gray-300 bg-white px-4 text-lg text-gray-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <button
              type="button"
              onClick={addRating}
              className="mt-auto flex h-12 items-center justify-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-5 text-lg font-semibold text-white transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              <AddIcon /> Add
            </button>
          </div>

          <div className="mt-5 space-y-2">
            {ratings.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-center text-lg leading-7 text-gray-600">
                Add a rating and its review count, for example 4.5 and 12.
              </div>
            ) : (
              ratings.map((item, index) => {
                const highestCount = Math.max(...ratings.map((ratingItem) => ratingItem.count));
                const width = highestCount ? (item.count / highestCount) * 100 : 0;

                return (
                  <div
                    key={`${item.rating}-${index}`}
                    className="grid grid-cols-[4.5rem_1fr_3rem_2.25rem] items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                  >
                    <span className="font-semibold text-gray-800">{item.rating} stars</span>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-amber-400 transition-all"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="text-right font-semibold text-gray-700">{item.count}</span>
                    <button
                      type="button"
                      aria-label={`Delete ${item.rating} star rating`}
                      title="Delete rating"
                      onClick={() => deleteRating(index)}
                      className="flex size-9 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5">
            <div className="font-gilroy-medium text-5xl font-bold leading-none text-gray-900">
              {Number.isFinite(rating) ? rating.toFixed(1) : '0.0'}
            </div>
            <p className="mb-3 mt-2 text-xl font-semibold text-gray-800">
              {reviewsCount} Review{reviewsCount === 1 ? '' : 's'}
            </p>
            <StarRating value={Math.round(rating)} label={`${rating} out of 5 stars`} />
            <p className="mb-0 mt-3 text-base leading-6 text-gray-600">
              Calculated automatically from the rating rows above.
            </p>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 p-5">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="m-0 font-gilroy-medium text-2xl font-semibold text-gray-800">
                {editingIndex === null ? 'Add a review' : 'Edit review'}
              </h3>
              <p className="mt-1 text-lg leading-7 text-gray-600">
                Add the guest information exactly as it should appear publicly.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {editingIndex !== null && (
                <button
                  type="button"
                  onClick={resetReviewForm}
                  className="text-lg font-semibold text-primary-600 hover:text-primary-700"
                >
                  Cancel edit
                </button>
              )}
              <Button
                onClick={saveReviews}
                size={ButtonSize.small}
                disabled={!hasPendingReviewUpdates}
                tracking={{ action: 'save reviews', category: 'unit type', label: '' }}
              >
                Save reviews
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Reviewer name <span className="sr-only">required</span>
              <input
                type="text"
                value={reviewDraft.name}
                placeholder="e.g. Guest name"
                onChange={(event) => updateDraft({ name: event.target.value })}
                className="h-12 rounded-lg border border-gray-300 px-4 text-lg font-normal outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <label className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Stayed nights
              <input
                type="number"
                min="1"
                step="1"
                value={reviewDraft.stayedNights}
                onChange={(event) => updateDraft({ stayedNights: event.target.value })}
                className="h-12 rounded-lg border border-gray-300 px-4 text-lg font-normal outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <label className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Review date
              <input
                type="date"
                value={reviewDraft.date}
                onChange={(event) => updateDraft({ date: event.target.value })}
                className="h-12 rounded-lg border border-gray-300 px-4 text-lg font-normal outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <div className="flex flex-col gap-2 text-lg font-semibold text-gray-700">
              Guest rating
              <div className="flex h-12 items-center rounded-lg border border-gray-300 px-3">
                <StarRating
                  value={reviewDraft.rating}
                  onChange={(value) => updateDraft({ rating: value })}
                  label="Guest rating"
                />
              </div>
            </div>
          </div>

          <label className="mt-4 flex flex-col gap-2 text-sm font-semibold text-gray-700">
            Review text <span className="sr-only">required</span>
            <textarea
              value={reviewDraft.description}
              rows={5}
              maxLength={1000}
              placeholder="Enter the guest review..."
              onChange={(event) => updateDraft({ description: event.target.value })}
              className="resize-y rounded-lg border border-gray-300 p-4 text-lg font-normal leading-7 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
            <span className="self-end text-base font-normal text-gray-600">
              {reviewDraft.description.length}/1000
            </span>
          </label>

          <div className="mt-5 flex justify-end gap-3">
            <Button
              onClick={resetReviewForm}
              type={ButtonType.gray}
              size={ButtonSize.small}
              tracking={{ action: 'clear review form', category: 'unit type', label: '' }}
            >
              Clear
            </Button>
            <button
              type="button"
              onClick={submitReview}
              className="flex items-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-6 py-3 text-lg font-semibold text-white transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {editingIndex === null ? <AddIcon /> : <EditIcon />}
              {editingIndex === null ? 'Add review' : 'Update review'}
            </button>
          </div>
        </section>
      </div>

      <section className="mt-6">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h3 className="m-0 font-gilroy-medium text-2xl font-semibold text-gray-800">
              Website preview
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {reviews.length} review{reviews.length === 1 ? '' : 's'} currently entered in admin.
            </p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <article className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-5">
            <div className="flex items-center gap-3">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-gilroy-medium text-xl font-bold text-emerald-700">
                {displayedReview.name.charAt(0).toUpperCase() || 'G'}
              </div>
              <div>
                <h4 className="m-0 text-lg font-bold text-gray-900">{displayedReview.name}</h4>
                <p className="m-0 text-lg text-gray-600">
                  Stayed {displayedReview.stayedNights} night
                  {displayedReview.stayedNights === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <StarRating
                value={displayedReview.rating}
                label={`${displayedReview.rating} out of 5 stars`}
              />
              <span className="text-gray-300">·</span>
              <span className="text-lg text-gray-600">
                {formatReviewDate(displayedReview.date)}
              </span>
            </div>
            <p className="mb-0 mt-4 text-lg leading-7 text-gray-800">
              {displayedReview.description}
            </p>
          </article>

          <div className="max-h-[340px] space-y-3 overflow-y-auto pr-1">
            {reviews.length === 0 ? (
              <div className="flex min-h-[180px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-500">
                No saved reviews yet. Complete the form to add the first one.
              </div>
            ) : (
              reviews.map((review, index) => (
                <article
                  key={review.id || `${review.name}-${review.date}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 transition hover:border-primary-200 hover:shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="m-0 truncate text-base font-bold text-gray-900">
                          {review.name}
                        </h4>
                        <span className="text-lg text-gray-600">
                          Stayed {review.stayedNights} night{review.stayedNights === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <StarRating
                          value={review.rating}
                          label={`${review.rating} out of 5 stars`}
                        />
                        <span className="text-base text-gray-600">
                          {formatReviewDate(review.date)}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        aria-label={`Edit review by ${review.name}`}
                        title="Edit review"
                        onClick={() => editReview(review, index)}
                        className="flex size-9 items-center justify-center rounded-lg text-gray-400 transition hover:bg-primary-50 hover:text-primary-600"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete review by ${review.name}`}
                        title="Delete review"
                        onClick={() => deleteReview(index)}
                        className="flex size-9 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                  <p className="mb-0 mt-3 line-clamp-3 text-lg leading-7 text-gray-700">
                    {review.description}
                  </p>
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
